import { useEffect, useRef, useState } from "react";
import { Check, Minus, Trophy } from "lucide-react";
import frothImg from "../../../assets/why-testops/froth.svg";
import jiraImg from "../../../assets/why-testops/jira.svg";
import testrailImg from "../../../assets/why-testops/testrail.svg";

// Values are [Froth TestOps, Jira, TestRail]. Competitor columns need marketing/legal sign-off before changes.
const groups = [
  {
    title: "End-2-End Validation",
    rows: [
      ["Requirement Lifecycle Management", [true, true, false]],
      ["Test Case Lifecycle Management", [true, false, true]],
      ["Defect Lifecycle Management", [true, true, false]],
    ],
  },
  {
    title: "Test Suite Management",
    rows: [
      ["Centralized Test Management Across Projects", [true, false, true]],
      ["Granular Test Step Tracking with Accurate Result Tracking", [true, false, true]],
      ["End-to-End Test Traceability", [true, false, false]],
      ["Issue Mapping from Test Execution", [true, false, false]],
      ["Cross-Project Test Cloning", [true, false, true]],
      ["Sign Off Feature for Release", [true, false, false]],
    ],
  },
  {
    title: "Bug Lifecycle Management",
    rows: [
      ["Smart Duplicate Issue Creation and Identification", [true, true, false]],
      ["Unified View of Tests, Bugs & Requirements", [true, false, false]],
      ["Multi-Issue Editing", [true, true, false]],
    ],
  },
  {
    title: "Automation",
    rows: [
      ["Web Application Testing", [true, true, true]],
      ["Mobile Application Testing (Android and iOS support)", [true, false, false]],
      ["REST API Testing", [true, false, false]],
      ["Schedule Test Execution", [true, false, false]],
      ["Workflow-Based Test Execution", [true, false, false]],
      ["Single Application for Entire STLC", [true, false, false]],
    ],
  },
  {
    title: "Connectivity & Integrations",
    rows: [
      ["Seamless Integration with Third-Party Tools", [true, true, true]],
      ["Integration with Automated Testing Solutions", [true, true, true]],
      ["Developer-Friendly API for Custom Integrations", [true, true, true]],
    ],
  },
  {
    title: "Support",
    rows: [
      ["Live Assistance Available", [true, false, false]],
      ["Fast Response Time", [true, true, false]],
      ["Self-Paced Learning Modules", [true, true, false]],
      ["User Guides and Tutorials", [true, true, true]],
      ["Scalable Support for Decentralized Teams", [true, true, true]],
    ],
  },
  {
    title: "Pricing",
    rows: [
      ["In-house Infrastructure Solution", [true, true, true]],
      ["Cloud Infrastructure Solution", [true, true, true]],
      ["Customizable License Plans", [true, false, false]],
      ["Pricing Per Account", [true, false, false]],
    ],
  },
];

const products = [
  { name: "Froth TestOps", logo: frothImg },
  { name: "Jira", logo: jiraImg },
  { name: "TestRail", logo: testrailImg },
];

const allRows = groups.flatMap(g => g.rows);
const tally = rows => products.map((_, i) => rows.filter(([, values]) => values[i]).length);
const totals = tally(allRows);
const isShared = ([, values]) => values.every(Boolean) || values.every(v => !v);

function Mark({ yes, featured }) {
  if (!yes) {
    return (
      <span className="cmp-mark cmp-mark--no">
        <Minus size={14} strokeWidth={2.5} aria-hidden="true" />
        <span className="visually-hidden">Not included</span>
      </span>
    );
  }
  return (
    <span className={`cmp-mark cmp-mark--yes${featured ? " cmp-mark--featured" : ""}`}>
      <Check size={14} strokeWidth={3} aria-hidden="true" />
      <span className="visually-hidden">Included</span>
    </span>
  );
}

// Reveals the coverage bars once the scoreboard scrolls into view.
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
}

export default function WhyFrothTestops() {
  const [active, setActive] = useState("all");
  const [diffOnly, setDiffOnly] = useState(false);
  const [scoreRef, scoreInView] = useInView();
  const matrixRef = useRef(null);

  const visibleGroups = groups
    .filter(g => active === "all" || g.title === active)
    .map(g => ({ ...g, shown: diffOnly ? g.rows.filter(r => !isShared(r)) : g.rows }))
    .filter(g => g.shown.length > 0);

  // A new filter starts the table from the top.
  useEffect(() => {
    if (matrixRef.current) matrixRef.current.scrollTop = 0;
  }, [active, diffOnly]);

  const filters = [
    { title: "all", label: "All", count: allRows.length },
    ...groups.map(g => ({ title: g.title, label: g.title, count: g.rows.length })),
  ];

  return (
    <section className="ft-section cmp-section" aria-labelledby="cmp-title">
      <div className="ft-container cmp-layout">
        <div className="cmp-aside">
          <span className="cmp-eyebrow">Compare</span>
          <h2 id="cmp-title" className="cmp-title">
            Why choose <span>Froth TestOps</span> for your testing team?
          </h2>

          <ul className={`cmp-score${scoreInView ? " is-in" : ""}`} ref={scoreRef} aria-labelledby="cmp-score-title">
            <li className="cmp-score__head">
              <span id="cmp-score-title">Capability coverage</span>
              <span>out of {allRows.length}</span>
            </li>
            {products.map((p, i) => {
              const pct = Math.round((totals[i] / allRows.length) * 100);
              return (
                <li className={`cmp-score__row${i === 0 ? " is-featured" : ""}`} key={p.name}>
                  <span className="cmp-score__brand">
                    <img className="cmp-score__logo" src={p.logo} alt={p.name} />
                    {i === 0 && (
                      <span className="cmp-score__badge">
                        <Trophy size={11} aria-hidden="true" /> Best
                      </span>
                    )}
                  </span>
                  <span
                    className="cmp-score__meter"
                    role="meter"
                    aria-valuemin={0}
                    aria-valuemax={allRows.length}
                    aria-valuenow={totals[i]}
                    aria-label={`${p.name} coverage`}
                  >
                    <span style={{ "--pct": pct / 100, "--i": i }} />
                  </span>
                  <span className="cmp-score__value">
                    <strong>{totals[i]}</strong>/{allRows.length}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="cmp-board">
          <div className="cmp-toolbar">
            <div className="cmp-tabs" role="group" aria-label="Filter by category">
              {filters.map(c => (
                <button
                  type="button"
                  key={c.title}
                  className={`cmp-tab${active === c.title ? " is-active" : ""}`}
                  aria-pressed={active === c.title}
                  onClick={e => {
                    setActive(c.title);
                    e.currentTarget.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
                  }}
                >
                  {c.label}
                  <span className="cmp-tab__count">{c.count}</span>
                </button>
              ))}
            </div>
            <label className="cmp-switch">
              <input type="checkbox" checked={diffOnly} onChange={e => setDiffOnly(e.target.checked)} />
              <span className="cmp-switch__track" aria-hidden="true" />
              Only differences
            </label>
          </div>

          <div className="cmp-matrix" ref={matrixRef} tabIndex={0} aria-label="Capability comparison table">
            <table className="cmp-table">
              <caption className="visually-hidden">Froth TestOps compared with Jira and TestRail</caption>
              <colgroup>
                <col />
                <col className="cmp-col--featured" />
                <col />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className="cmp-table__corner">
                    Capability
                  </th>
                  {products.map((p, i) => (
                    <th scope="col" key={p.name} className={i === 0 ? "is-featured" : undefined}>
                      <img src={p.logo} alt={p.name} />
                    </th>
                  ))}
                </tr>
              </thead>
              {visibleGroups.map(group => {
                const groupTally = tally(group.rows);
                return (
                  <tbody key={group.title}>
                    <tr className="cmp-table__group">
                      <th scope="rowgroup">{group.title}</th>
                      {groupTally.map((n, i) => (
                        <td key={products[i].name} className={i === 0 ? "is-featured" : undefined}>
                          <span className="cmp-tally">
                            {n}/{group.rows.length}
                          </span>
                        </td>
                      ))}
                    </tr>
                    {group.shown.map(([label, values]) => (
                      <tr key={label}>
                        <th scope="row">{label}</th>
                        {values.map((v, i) => (
                          <td key={products[i].name} className={i === 0 ? "is-featured" : undefined}>
                            <Mark yes={v} featured={i === 0} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                );
              })}
              {visibleGroups.length === 0 && (
                <tbody>
                  <tr className="cmp-table__empty">
                    <td colSpan={products.length + 1}>All three products cover every capability in this category.</td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
